import React, { useState, useEffect, useContext } from 'react'
import { message, Tooltip } from 'antd'
import { useMsal } from '@azure/msal-react'
import CommonAttachmentModal from '../../../components/commonAttachmentModal/commonAttachmentModal'
import Spinner from '../../../components/common/spinner/spinner'
import {
  handleAttachments,
  generatePresignedUrl
} from '../../../api/vendorApiService/vendorApiUtils'
import { UserContext } from '../../../context/userContext'

/**
 * Helper function to get attachment S3 path
 */
const getAttachmentS3Path = (doc, vendorId) => {
  if (doc.s3Location) return doc.s3Location
  if (doc.url && doc.url.includes('s3')) return doc.url
  if (doc.attachmentId && vendorId) {
    return `vendors/${vendorId}/attachments/${doc.attachmentId}/${doc.name}`
  }
  if (doc.name && vendorId) {
    return `vendors/${vendorId}/attachments/${doc.name}`
  }
  return null
}

const VendorAttachment = ({
  visible,
  onClose,
  vendorId,
  vendorName,
  attachments = [],
  attachmentType = 'additional',
  onAttachmentsUpdate,
  onUploadSuccess
}) => {
  const { instance: msalInstance } = useMsal()

  // Get vendor write permission from context
  const { getUserPermissions } = useContext(UserContext)
  const vendorPermissions = getUserPermissions('vendor')
  const canEditAttachments = vendorPermissions?.write === true

  const [fileList, setFileList] = useState([])
  const [actionLoading, setActionLoading] = useState({
    uploadAttachment: false,
    previewAttachment: {},
    downloadAttachment: {},
    deleteAttachment: {}
  })

  useEffect(() => {
    if (visible) {
      const attachmentsArray = Array.isArray(attachments) ? attachments : []

      const formattedAttachments = attachmentsArray.map((attachment, index) => {
        const uniqueId = attachment._id || attachment.uid || `attachment-${index}-${Date.now()}`
        
        return {
          uid: uniqueId,
          id: uniqueId,
          name: attachment.filename || attachment.name || `Attachment ${index + 1}`,
          status: 'done',
          size: attachment.size || 0,
          type: attachment.mimeType || attachment.type || 'application/octet-stream',
          url: attachment.s3Location || attachment.url,
          s3Location: attachment.s3Location || attachment.url,
          attachmentId: attachment._id,
          _id: attachment._id,
          ...attachment
        }
      })

      setFileList(formattedAttachments)
    } else {
      setFileList([])
      setActionLoading({
        uploadAttachment: false,
        previewAttachment: {},
        downloadAttachment: {},
        deleteAttachment: {}
      })
    }
  }, [visible, attachments])

  // Dynamic config based on permissions
  const config = {
    enableUpload: canEditAttachments,
    enableDownload: true,
    enablePreview: true,
    enableDelete: canEditAttachments,
    disableModifications: !canEditAttachments,
    disabledTooltipMessage: 'You do not have permission to modify attachments',
    showOriginalFilename: true,
    maxFileSize: 10,
    acceptedTypes: ['image/*', '.pdf', 'application/pdf'],
    allowedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/gif',
      'image/bmp',
      'image/webp'
    ],
    uploadText: canEditAttachments
      ? 'Click or drag files to this area to upload'
      : 'Attachment modification disabled',
    uploadHint: canEditAttachments
      ? 'Support for single or bulk upload. Image and PDF files are allowed. Files will be automatically saved.'
      : 'You do not have permission to upload or delete attachments.',
    multiple: attachmentType !== 'bir'
  }

  const handleUploadFile = async (file) => {
    if (!canEditAttachments) {
      message.warning('You do not have permission to upload attachments')
      return false
    }

    if (!vendorId) {
      message.error('No vendor selected')
      return false
    }

    const isImage = file.type.startsWith('image/')
    const isPdf = file.type === 'application/pdf'

    if (!isImage && !isPdf) {
      message.error(`${file.name} is not an image or PDF file`)
      return false
    }

    if (attachmentType === 'bir' && fileList.length >= 1) {
      message.warning('Only one BIR attachment is allowed. Please delete the existing file first.')
      return false
    }

    try {
      setActionLoading(prev => ({ ...prev, uploadAttachment: true }))

      const response = await handleAttachments(
        vendorId,
        'update',
        null,
        file,
        msalInstance,
        attachmentType
      )

      if (response) {
        const newAttachment = {
          uid: response.data?._id || `${Date.now()}-${Math.random()}`,
          id: response.data?._id || `temp-${Date.now()}-${Math.random()}`,
          name: file.name,
          status: 'done',
          size: file.size,
          type: file.type,
          url: response.data?.s3Location || response.data?.fileUrl || URL.createObjectURL(file),
          s3Location: response.data?.s3Location || response.data?.fileUrl,
          attachmentId: response.data?._id,
          _id: response.data?._id
        }

        const updatedFileList = [...fileList, newAttachment]
        setFileList(updatedFileList)

        if (onAttachmentsUpdate) {
          onAttachmentsUpdate(updatedFileList, attachmentType)
        }

        message.success(response?.message || 'File uploaded successfully')

        if (onUploadSuccess) {
          setTimeout(() => {
            onUploadSuccess(vendorId, attachmentType)
          }, 1000)
        }
      }

      return false
    } catch (error) {
      console.error('Error uploading file:', error)
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error'
      message.error(`Failed to upload file: ${errorMessage}`)
      return false
    } finally {
      setActionLoading(prev => ({ ...prev, uploadAttachment: false }))
    }
  }

  const handlePreviewDocument = async (doc) => {
    try {
      setActionLoading(prev => ({
        ...prev,
        previewAttachment: { ...prev.previewAttachment, [doc.id]: true }
      }))

      if (doc.originFileObj) {
        const url = URL.createObjectURL(doc.originFileObj)
        window.open(url, '_blank')
        URL.revokeObjectURL(url)
        return
      }

      const attachmentS3Path = getAttachmentS3Path(doc, vendorId)
      if (!attachmentS3Path) {
        throw new Error(`Could not determine attachment path for ${doc.name}`)
      }

      const presignedUrl = await generatePresignedUrl(attachmentS3Path, msalInstance)
      if (!presignedUrl) {
        throw new Error(`Failed to generate preview URL for ${doc.name}`)
      }

      window.open(presignedUrl, '_blank')
    } catch (error) {
      console.error(`Error previewing document ${doc.name}:`, error)
      message.error(`Failed to preview ${doc.name}. Please try again.`)
    } finally {
      setActionLoading(prev => {
        const previewCopy = { ...prev.previewAttachment }
        delete previewCopy[doc.id]
        return { ...prev, previewAttachment: previewCopy }
      })
    }
  }

  const handleDownloadDocument = async (doc) => {
    try {
      setActionLoading(prev => ({
        ...prev,
        downloadAttachment: { ...prev.downloadAttachment, [doc.id]: true }
      }))

      if (doc.originFileObj) {
        const fileUrl = URL.createObjectURL(doc.originFileObj)
        const link = document.createElement('a')
        link.href = fileUrl
        link.download = doc.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(fileUrl), 1000)
        return
      }

      const attachmentS3Path = getAttachmentS3Path(doc, vendorId)
      if (!attachmentS3Path) {
        throw new Error(`Could not determine attachment path for ${doc.name}`)
      }

      const presignedUrl = await generatePresignedUrl(attachmentS3Path, msalInstance)
      if (!presignedUrl) {
        throw new Error(`Failed to generate URL for ${doc.name}`)
      }

      window.open(presignedUrl, '_blank')
    } catch (error) {
      console.error(`Error downloading document ${doc.name}:`, error)
      message.error(`Failed to download ${doc.name}. Please try again.`)
    } finally {
      setActionLoading(prev => {
        const downloadCopy = { ...prev.downloadAttachment }
        delete downloadCopy[doc.id]
        return { ...prev, downloadAttachment: downloadCopy }
      })
    }
  }

  const handleDeleteDocument = async (documentId, attachmentId) => {
    if (!canEditAttachments) {
      message.warning('You do not have permission to delete attachments')
      return
    }

    if (!vendorId) {
      message.error('No vendor selected')
      return
    }

    setActionLoading(prev => ({
      ...prev,
      deleteAttachment: { ...prev.deleteAttachment, [documentId]: true }
    }))

    try {
      const attachmentToDelete = fileList.find(att => att.id === documentId || att.uid === documentId)
      if (!attachmentToDelete) throw new Error('Attachment not found')

      const attachmentIdToDelete = attachmentId || attachmentToDelete.attachmentId || attachmentToDelete._id
      const isValidObjectId = attachmentIdToDelete && 
                             typeof attachmentIdToDelete === 'string' && 
                             attachmentIdToDelete.length === 24 && 
                             /^[0-9a-fA-F]{24}$/.test(attachmentIdToDelete)

      if (!isValidObjectId) {
        throw new Error(`Invalid attachment ID: ${attachmentIdToDelete}`)
      }

      const response = await handleAttachments(
        vendorId,
        'delete',
        attachmentIdToDelete,
        null,
        msalInstance,
        attachmentType
      )

      const updatedFileList = fileList.filter(file => file.id !== documentId && file.uid !== documentId)
      setFileList(updatedFileList)

      if (onAttachmentsUpdate) {
        onAttachmentsUpdate(updatedFileList, attachmentType)
      }

      message.success(response?.message || 'Document deleted successfully')
    } catch (error) {
      console.error('Error deleting document:', error)
      message.error(error.response?.data?.message || error.message || 'Failed to delete document')
    } finally {
      setActionLoading(prev => {
        const deleteCopy = { ...prev.deleteAttachment }
        delete deleteCopy[documentId]
        return { ...prev, deleteAttachment: deleteCopy }
      })
    }
  }

  const handlers = {
    handleUploadFile,
    handlePreviewDocument,
    handleDownloadDocument,
    handleDeleteDocument,
    onAttachmentsUpdate: (updatedFiles) => {
      setFileList(updatedFiles)
      if (onAttachmentsUpdate) {
        onAttachmentsUpdate(updatedFiles, attachmentType)
      }
    }
  }

  const loadingStates = {
    uploadAttachment: actionLoading.uploadAttachment,
    previewAttachment: actionLoading.previewAttachment,
    downloadAttachment: actionLoading.downloadAttachment,
    deleteAttachment: actionLoading.deleteAttachment
  }

  const CustomSpinner = () => (
    <div className="upload-loading">
      <Spinner size="small" />
      <p>Uploading...</p>
    </div>
  )

  const handleClose = () => {
    if (onAttachmentsUpdate) {
      onAttachmentsUpdate(fileList, attachmentType)
    }
    onClose()
  }

  const getModalTitle = () => {
    const typeText = attachmentType === 'bir' ? 'BIR Attachments' : 'Additional Attachments'
    return `${typeText} - ${vendorName || vendorId}`
  }

  return (
    <Tooltip title={!canEditAttachments ? 'Read-only: You cannot modify attachments' : ''}>
      <span>
        <CommonAttachmentModal
          visible={visible}
          onClose={handleClose}
          title={getModalTitle()}
          existingAttachments={fileList}
          fileList={[]}
          setFileList={setFileList}
          config={config}
          handlers={handlers}
          loadingStates={loadingStates}
          customSpinner={CustomSpinner}
        />
      </span>
    </Tooltip>
  )
}

export default VendorAttachment
